'use client';

import { useMemo } from 'react';

import type { FormData } from '../FormProject';

type Props = {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
};

export default function Step4ClientInfo({ formData, updateFormData }: Props) {
  const imagePreviews = useMemo(
    () => formData.inspirationImages.map((file) => URL.createObjectURL(file)),
    [formData.inspirationImages],
  );

  const inspirationUrlList = useMemo(
    () =>
      formData.inspirationUrls
        .split(/[\n,]+/)
        .map((u) => u.trim())
        .filter(Boolean),
    [formData.inspirationUrls],
  );

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string) => {
    // French phone number validation (basic)
    return /^(\+33|0)[1-9](\d{8})$/.test(phone.replace(/\s/g, ''));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Vos coordonnées</h2>
        <p className="mt-2 text-gray-600">
          Pour que nous puissions vous recontacter et discuter de votre projet
        </p>
      </div>

      {/* Name fields */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
            Prénom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="firstName"
            required
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 focus:outline-none"
            placeholder="Votre prénom"
            value={formData.firstName}
            onChange={(e) => updateFormData({ firstName: e.target.value })}
          />
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="lastName"
            required
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 focus:outline-none"
            placeholder="Votre nom"
            value={formData.lastName}
            onChange={(e) => updateFormData({ lastName: e.target.value })}
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          required
          className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:ring-emerald-500 focus:outline-none ${
            formData.email && !validateEmail(formData.email)
              ? 'border-red-300 focus:border-red-500'
              : 'border-gray-300 focus:border-emerald-500'
          }`}
          placeholder="votre@email.com"
          value={formData.email}
          onChange={(e) => updateFormData({ email: e.target.value })}
        />
        {formData.email && !validateEmail(formData.email) && (
          <p className="mt-1 text-sm text-red-600">Veuillez entrer un email valide</p>
        )}
      </div>

      {/* Phone */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
          Téléphone <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          id="phone"
          required
          className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:ring-emerald-500 focus:outline-none ${
            formData.phone && !validatePhone(formData.phone)
              ? 'border-red-300 focus:border-red-500'
              : 'border-gray-300 focus:border-emerald-500'
          }`}
          placeholder="06 12 34 56 78"
          value={formData.phone}
          onChange={(e) => updateFormData({ phone: e.target.value })}
        />
        {formData.phone && !validatePhone(formData.phone) && (
          <p className="mt-1 text-sm text-red-600">
            Format attendu : 06 12 34 56 78 ou +33 6 12 34 56 78
          </p>
        )}
      </div>

      {/* Summary */}
      <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-6">
        <h3 className="font-semibold text-emerald-900">Récapitulatif de votre demande</h3>
        <dl className="mt-4 space-y-3 text-sm">
          {formData.description && (
            <div>
              <dt className="font-medium text-emerald-900">Projet :</dt>
              <dd className="text-emerald-800">{formData.description.substring(0, 100)}...</dd>
            </div>
          )}
          {formData.bodyPlacement && (
            <div>
              <dt className="font-medium text-emerald-900">Emplacement :</dt>
              <dd className="text-emerald-800">
                {formData.bodyPlacement === 'Autre'
                  ? formData.customPlacement
                  : formData.bodyPlacement}
              </dd>
            </div>
          )}
          {formData.size && (
            <div>
              <dt className="font-medium text-emerald-900">Taille :</dt>
              <dd className="text-emerald-800">{formData.size}</dd>
            </div>
          )}
          {formData.inspirationText && (
            <div>
              <dt className="font-medium text-emerald-900">Inspiration :</dt>
              <dd className="text-emerald-800">{formData.inspirationText}</dd>
            </div>
          )}
          {inspirationUrlList.length > 0 && (
            <div>
              <dt className="font-medium text-emerald-900">Liens d'inspiration :</dt>
              <dd className="mt-1 space-y-1">
                {inspirationUrlList.map((url) => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate text-emerald-700 underline hover:text-emerald-900"
                  >
                    {url}
                  </a>
                ))}
              </dd>
            </div>
          )}
          {imagePreviews.length > 0 && (
            <div>
              <dt className="font-medium text-emerald-900">
                Photos d'inspiration ({imagePreviews.length}) :
              </dt>
              <dd className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                {imagePreviews.map((preview, index) => (
                  <img
                    key={index}
                    src={preview}
                    alt={`Inspiration ${index + 1}`}
                    className="h-20 w-full rounded-md object-cover"
                    width={80}
                    height={80}
                  />
                ))}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}
